/**
 * Shared role-selection control for the Add user + Edit roles dialogs.
 *
 * Why a dedicated component:
 *  - Visual treatment of the role list is consistent across both flows
 *  - Admin is a **wildcard** role for section ACCESS — granting it
 *    implies access to every section, so picking it alongside
 *    Depletions / Distribution / Marketing is redundant.  When admin
 *    is ticked, those section roles are visually suppressed and
 *    cleared from the selection.
 *  - Two roles are exceptions and stay selectable alongside admin
 *    (never cleared when admin is ticked):
 *      - field_rep carries data ownership (territory, home address,
 *        "the rep who owns this visit note"), so an admin who also
 *        works the field keeps it explicitly.
 *      - data_upload is deliberately NOT implied by admin (the API
 *        checks it with require_explicit_role): loading depletion
 *        files changes DEV and PROD data, so it's a separate grant.
 *  - Checkboxes (instead of pill toggle chips) make the multi-select
 *    intent explicit at a glance.
 */

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useMemo } from 'react';

import type { RolePublic } from '../../api/auth';
import { ROLES_ALONGSIDE_ADMIN } from '../../auth/roles';
import type { RoleWithUsage } from '../../api/admin';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const ADMIN_ROLE_NAME = 'admin';
// Roles that stay selectable beside admin — see auth/roles.ts.
const COEXIST_WITH_ADMIN = ROLES_ALONGSIDE_ADMIN;

type AnyRole = RoleWithUsage | RolePublic;

interface RoleCheckboxListProps {
  roles: AnyRole[];
  /** Set of role IDs currently selected. */
  selected: Set<string>;
  /** Receives the new selection set. */
  onChange: (next: Set<string>) => void;
}

export function RoleCheckboxList({ roles, selected, onChange }: RoleCheckboxListProps) {
  const adminRole = useMemo(() => roles.find((r) => r.name === ADMIN_ROLE_NAME), [roles]);
  // Split the non-admin roles into two visually distinct groups:
  //   1. coexistRoles — stay visible and toggleable when admin is on
  //      (today: field_rep)
  //   2. sectionRoles — section-access grants that admin implies, so
  //      they hide behind the admin callout when admin is on
  const coexistRoles = useMemo(
    () => roles.filter((r) => r.name !== ADMIN_ROLE_NAME && COEXIST_WITH_ADMIN.has(r.name)),
    [roles],
  );
  const sectionRoles = useMemo(
    () => roles.filter((r) => r.name !== ADMIN_ROLE_NAME && !COEXIST_WITH_ADMIN.has(r.name)),
    [roles],
  );
  const adminSelected = !!adminRole && selected.has(adminRole.id);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const handleAdminToggle = () => {
    if (!adminRole) return;
    const next = new Set(selected);
    if (next.has(adminRole.id)) {
      // Unchecking admin — restore visibility of section roles in
      // their previous state.  Coexist roles (e.g. field_rep) are
      // untouched.
      next.delete(adminRole.id);
    } else {
      // Checking admin — clear only the section roles (those grants
      // are implied by admin's wildcard).  Coexist roles like
      // field_rep stay because they carry data ownership the
      // wildcard doesn't replicate.
      const coexistIds = new Set(coexistRoles.map((r) => r.id));
      for (const id of [...next]) {
        if (!coexistIds.has(id)) next.delete(id);
      }
      next.add(adminRole.id);
    }
    onChange(next);
  };

  if (roles.length === 0) {
    return (
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12,
          color: 'rgba(15, 10, 7, 0.5)',
          fontStyle: 'italic',
        }}
      >
        No roles available — add one in the Roles tab first.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Administrator — always rendered first, with extra emphasis */}
      {adminRole && (
        <Box
          sx={{
            mb: adminSelected ? 1.25 : 0.75,
            px: 1.25,
            py: 0.85,
            borderRadius: 1.25,
            border: '1px solid',
            borderColor: adminSelected ? GOLD : 'rgba(187, 140, 63, 0.22)',
            bgcolor: adminSelected ? 'rgba(187, 140, 63, 0.10)' : 'transparent',
            transition: 'background-color 140ms ease, border-color 140ms ease',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={adminSelected}
                onChange={handleAdminToggle}
                icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                sx={{
                  py: 0.5,
                  color: 'rgba(15, 10, 7, 0.35)',
                  '&.Mui-checked': { color: GOLD },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AdminPanelSettingsIcon
                  sx={{ fontSize: 17, color: adminSelected ? GOLD : 'rgba(15, 10, 7, 0.45)' }}
                />
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: INK,
                  }}
                >
                  {adminRole.display_name}
                </Typography>
                <Box
                  sx={{
                    ml: 0.5,
                    px: 0.7,
                    py: 0.15,
                    borderRadius: 0.6,
                    bgcolor: 'rgba(187, 140, 63, 0.16)',
                    color: '#8e6a2a',
                    fontFamily: FONT_INTER,
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                  }}
                >
                  All-access
                </Box>
              </Box>
            }
            sx={{ m: 0, alignItems: 'center', width: '100%' }}
          />
          {adminSelected && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.62)',
                mt: 0.4,
                pl: 4.25,
                lineHeight: 1.45,
              }}
            >
              Administrator already includes access to every section. The roles below aren't
              included and can still be added — Data Upload for loading depletion files, Regional
              Sales Representative if this admin also works in the field.
            </Typography>
          )}
        </Box>
      )}

      {/* Coexist roles — always visible, even when admin is selected. */}
      {coexistRoles.length > 0 && (
        <Box>
          {coexistRoles.map((role) => (
            <RoleCheckboxRow
              key={role.id}
              role={role}
              checked={selected.has(role.id)}
              onToggle={() => toggle(role.id)}
            />
          ))}
        </Box>
      )}

      {/* Section roles — hidden when admin is selected (admin implies
          access to all of them anyway). */}
      {!adminSelected && (
        <Box>
          {sectionRoles.map((role) => (
            <RoleCheckboxRow
              key={role.id}
              role={role}
              checked={selected.has(role.id)}
              onToggle={() => toggle(role.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function RoleCheckboxRow({
  role,
  checked,
  onToggle,
}: {
  role: AnyRole;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={onToggle}
          icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
          checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
          sx={{
            py: 0.5,
            color: 'rgba(15, 10, 7, 0.35)',
            '&.Mui-checked': { color: GOLD },
          }}
        />
      }
      label={
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13.5,
              fontWeight: 600,
              color: INK,
              lineHeight: 1.3,
            }}
          >
            {role.display_name}
          </Typography>
          {'description' in role && role.description && (
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.55)',
                lineHeight: 1.45,
              }}
            >
              {role.description}
            </Typography>
          )}
        </Box>
      }
      sx={{
        m: 0,
        py: 0.4,
        alignItems: 'flex-start',
        width: '100%',
        '& .MuiFormControlLabel-label': { pt: 0.5 },
      }}
    />
  );
}
